import { Close } from "@mui/icons-material";
import { Box, IconButton, TextField, InputAdornment } from "@mui/material";
import { EntitySelectorWithTabs } from "modals/EntitySelector";
import { FC, useState } from "react";
import { CoachPetAvatar } from "components/VetStore/CoachPetAvatar";
import { DomainColorSquares } from "components/Requirements";
import { useVoiceFieldFocus } from "hooks/useVoiceFieldFocus";

type FieldWithEntitySelectorAndTabsProps = {
  label?: string;
  element: any;
  onChangeElement: (value: any) => void;
  idField: string;
  entityName: string;
  entityList: any[];
  displayField?: string;
  error?: string;
  compact?: boolean;
  tabs?: { label: string; filterTabCriteria: (data: any) => boolean }[];
  showPet?: boolean;
  showDomainSquares?: boolean;
  voiceFieldId?: string;
};

export const FieldWithEntitySelectorAndTabs: FC<
  FieldWithEntitySelectorAndTabsProps
> = ({
  label,
  element,
  onChangeElement,
  idField,
  entityName,
  entityList,
  displayField = "name",
  error = "",
  tabs = [],
  showPet = false,
  showDomainSquares = false,
  voiceFieldId,
}) => {
  const [openEntitySelector, setOpenEntitySelector] = useState<boolean>(false);
  const { activeFieldId } = useVoiceFieldFocus();
  const voiceOpen = Boolean(voiceFieldId && activeFieldId === voiceFieldId);
  const selectorOpen = openEntitySelector || voiceOpen;
  const safeEntityList = Array.isArray(entityList)
    ? entityList.filter(Boolean)
    : [];

  return (
    <Box>
      <TextField
        label={label}
        fullWidth
        placeholder={`Select ${entityName}`}
        value={element ? element[displayField] : ""}
        onClick={() => setOpenEntitySelector(true)}
        size="small"
        margin="dense"
        sx={{
          cursor: "pointer",
          "& .MuiInputBase-input": {
            cursor: "pointer",
          },
        }}
        slotProps={{
          input: {
            readOnly: true,
            startAdornment:
              showPet && element ? (
                <InputAdornment position="start">
                  <CoachPetAvatar
                    pictureUrl={element.picture_url}
                    name={element[displayField]}
                    equippedStoreItems={element.equipped_store_items}
                    avatarSize={28}
                  />
                </InputAdornment>
              ) : undefined,
            endAdornment: element && (
              <InputAdornment position="end">
                <IconButton
                  edge="end"
                  aria-label="clear"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChangeElement(null);
                  }}
                  size="small"
                >
                  <Close />
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
        error={error !== ""}
        helperText={error}
      />
      {showDomainSquares && element && (
        <Box mt={0.75}>
          <DomainColorSquares requirement={element} />
        </Box>
      )}

      {selectorOpen && (
        <EntitySelectorWithTabs
          open={selectorOpen}
          onClose={() => {
            if (!voiceOpen) setOpenEntitySelector(false);
          }}
          title={`Select ${entityName}`}
          filterCriteria={(search, data) =>
            (data?.[displayField] ?? "")
              .toString()
              .toLowerCase()
              .includes(search.toLowerCase())
          }
          listEntities={safeEntityList}
          onSelect={(selected: any) => {
            onChangeElement(selected);
          }}
          keyField={idField}
          displayField={displayField}
          tabs={tabs}
          closeOnSelect={!voiceOpen}
          selectedKey={element?.[idField]}
          lockOpen={voiceOpen}
          renderItemPrefix={
            showPet
              ? (data: any) => (
                  <CoachPetAvatar
                    pictureUrl={data.picture_url}
                    name={data[displayField]}
                    equippedStoreItems={data.equipped_store_items}
                    avatarSize={36}
                  />
                )
              : undefined
          }
        />
      )}
    </Box>
  );
};
